import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Crown,
  Zap,
  Star,
  Users,
  FileText,
  Clock,
  X,
  Mail,
  Key,
} from "lucide-react";
import Modal from "@core/modal";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { Input } from "@core/input";
import { motion, AnimatePresence } from "framer-motion";
import { saveEmailForSubscription } from "@packages/api/madlenAPI";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { setPremium } from "@packages/store/userSlice";

interface ProMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CongratsAnimationProps {
  onClose: () => void;
}

const CongratsAnimation = ({ onClose }: CongratsAnimationProps) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.div
        className="w-full h-full absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="relative max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          {/* Confetti effect */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ pointerEvents: "none" }}
          >
            {Array.from({ length: 30 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: -20,
                  scale: Math.random() * 0.5 + 0.5,
                  opacity: 1,
                }}
                animate={{
                  y: window.innerHeight + 20,
                  rotate: Math.random() * 360,
                  opacity: 0,
                }}
                transition={{
                  duration: Math.random() * 2 + 1,
                  ease: "easeOut",
                  delay: Math.random() * 0.5,
                }}
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: Math.random() > 0.5 ? "50%" : "0",
                  background: [
                    "#FF5733",
                    "#33FF57",
                    "#3357FF",
                    "#FF33A8",
                    "#33FFF5",
                    "#F5FF33",
                  ][Math.floor(Math.random() * 6)],
                }}
              />
            ))}
          </div>

          {/* Success message */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl border border-gray-200 dark:border-gray-700 text-center z-10 relative"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 12 }}
          >
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:space-x-6">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 8 }}
                className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <Check
                  size={32}
                  className="text-green-600 dark:text-green-400"
                />
              </motion.div>

              <div className="flex-1 text-center sm:text-left">
                <Text variant="h3" className="font-bold mb-1">
                  {t("pro.congrats.title", "Congratulations!")}
                </Text>
                <Text variant="p" className="text-gray-600 dark:text-gray-300">
                  {t(
                    "pro.congrats.message",
                    "Your account has been upgraded to Premium!",
                  )}
                </Text>
              </div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <Button
                  onClick={onClose}
                  size="lg"
                  rounded="xl"
                  bgColor="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                  textColor="text-white"
                  className="w-full sm:w-auto"
                >
                  {t("pro.congrats.button", "Start Exploring")}
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

const ProMemberModal = ({ isOpen, onClose }: ProMemberModalProps) => {
  const { t } = useTranslation();
  const [upgradeCode, setUpgradeCode] = useState("");
  const [showCongrats, setShowCongrats] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setShowCongrats(false);
      setUpgradeCode("");
      setIsValidating(false);
      setErrorMessage(null);
    }
  }, [isOpen]);

  // Debug effect to log state changes
  useEffect(() => {
    console.log("showCongrats state:", showCongrats);
  }, [showCongrats]);

  const handleUpgrade = () => {
    if (!upgradeCode.trim()) return;

    setIsValidating(true);
    setErrorMessage(null);
    console.log("Starting validation process...");

    setIsValidating(false);

    if (upgradeCode.trim().toUpperCase() === "GELECEKMADLENDE") {
      dispatch(setPremium(new Date()));
      setShowCongrats(true);
    } else if (upgradeCode.trim().toUpperCase() === "MADLENTEDBODRUM") {
      dispatch(
        setPremium(new Date(new Date().setMonth(new Date().getMonth() + 1))),
      );
      setShowCongrats(true);
    } else {
      // Show error message for invalid code
      setErrorMessage(
        t("pro.code.invalid", "Invalid premium code. Please try again."),
      );
      console.log("Invalid premium code");
    }
  };

  const handleCongratsClose = () => {
    setShowCongrats(false);
    onClose();
    window.location.reload();
  };

  const features = [
    {
      icon: <Zap size={20} className="text-amber-500" />,
      title: t("pro.features.unlimited.title", "Unlimited AI Assistants"),
      description: t(
        "pro.features.unlimited.description",
        "Use all the tools in our Toolbox without any limitations. Access them to enhance your teaching and content creation experience.",
      ),
    },
    {
      icon: <FileText size={20} className="text-emerald-500" />,
      title: t("pro.features.documents.title", "Essay Grading and Analysis"),
      description: t(
        "pro.features.documents.description",
        "Grade and analyze essays with comprehensive rubric-based feedback, detailed language analysis, and personalized improvement suggestions.",
      ),
    },
    {
      icon: <Users size={20} className="text-blue-500" />,
      title: t("pro.features.students.title", "Student Integration"),
      description: t(
        "pro.features.students.description",

        "Integrate tools and their results into your classes and students' learning experience. Track progress, and provide targeted feedback to all of your students.",
      ),
    },
    {
      icon: <Clock size={20} className="text-purple-500" />,
      title: t("pro.features.priority.title", "Priority Support"),
      description: t(
        "pro.features.priority.description",
        "Get faster responses and dedicated support for all your questions. Our premium support team is available to assist you with technical issues.",
      ),
    },
  ];

  return (
    <>
      {/* Only show the modal if isOpen is true AND showCongrats is false */}
      {isOpen && !showCongrats && (
        <Modal
          show={true}
          onClose={onClose}
          title={t("pro.modal.title", "Upgrade to Premium")}
          outerClassName="max-w-5xl max-h-[80vh]"
        >
          <div className="max-h-[calc(80vh-10rem)] overflow-y-auto pr-1">
            <div className="space-y-4">
              {/* Hero section */}
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 dark:from-amber-500 dark:to-amber-600 p-4 text-white">
                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-40 h-40 bg-white/10 rounded-full blur-xl"></div>

                <div className="relative flex items-center">
                  <Crown size={32} className="text-white mr-3 animate-pulse" />
                  <div>
                    <Text variant="h3" className="text-white font-bold mb-0">
                      {t("pro.hero.title", "Unlock Your Full Potential")}
                    </Text>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                {/* Contact Information */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800 w-full md:w-auto">
                  <div className="flex items-center">
                    <Mail
                      size={20}
                      className="text-blue-600 dark:text-blue-400 mr-3 mt-1 flex-shrink-0"
                    />
                    <div>
                      <Text variant="h4" className="font-semibold mb-1 text-sm">
                        {t(
                          "pro.contact.title",
                          "Contact Us for Premium Access",
                        )}
                      </Text>
                      <Text
                        variant="p"
                        className="font-medium text-blue-700 dark:text-blue-400 text-sm"
                      >
                        {t("pro.contact.email", "hello@madlen.io")}
                      </Text>
                    </div>
                  </div>
                </div>

                {/* Upgrade Code Input */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex-1">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="flex-1">
                        <Input
                          placeholder={t(
                            "pro.code.placeholder",
                            "Enter your upgrade code",
                          )}
                          value={upgradeCode}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setUpgradeCode(e.target.value);
                            setErrorMessage(null); // Clear error when typing
                          }}
                          leftIcon={
                            <Key
                              size={16}
                              className="text-gray-500 dark:text-gray-400"
                            />
                          }
                          size="40px"
                          disabled={isValidating}
                          classNames={{
                            input: errorMessage
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "text-gray-900 dark:text-white dark:bg-gray-800 dark:border-gray-700",
                          }}
                        />
                      </div>
                      <Button
                        onClick={handleUpgrade}
                        size="md"
                        rounded="xl"
                        bgColor="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                        textColor="text-white"
                        disabled={!upgradeCode.trim() || isValidating}
                        className="sm:min-w-[100px] w-full sm:w-auto"
                      >
                        {isValidating ? (
                          <div className="flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                            {t("pro.code.validating", "Validating...")}
                          </div>
                        ) : (
                          t("pro.code.upgrade", "Upgrade")
                        )}
                      </Button>
                    </div>

                    {/* Error message */}
                    {errorMessage && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-1"
                      >
                        {errorMessage}
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-start p-3 border border-gray-100 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/50"
                  >
                    <div className="mr-2 mt-0 flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <Text
                        variant="h4"
                        className="font-medium mb-0 text-balance"
                      >
                        {feature.title}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>

              {/* Usage Limits Notice */}
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 border border-amber-100 dark:border-amber-800">
                <Text
                  variant="p"
                  className="text-amber-800 dark:text-amber-300 text-xs"
                >
                  {t(
                    "pro.limits.notice",
                    "Free accounts have limited usage. Upgrade to premium for unlimited access to all features.",
                  )}
                </Text>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Congratulations Animation */}
      <AnimatePresence>
        {showCongrats && <CongratsAnimation onClose={handleCongratsClose} />}
      </AnimatePresence>
    </>
  );
};

export default ProMemberModal;
